import { Course, CourseDeep, SingleSessionResult } from '@mc/api'
import { paths } from '@mc/paths'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'

import { formatSubtitle, formatTitle, formatCourseDuration } from 'libs/utils'
import { AnyCourse } from './types'

export const QR_SEALED_BUCKET_V1_EXP_NAME = 'pen-4743_v1'

const isSession = (content: AnyCourse): content is SingleSessionResult =>
  !('type' in content)

const getContentType = (content: AnyCourse) => {
  if (isSession(content)) return TRACK_CONTENT_TYPES.CAMP_TRAILER

  if (content.mediaUuid) return TRACK_CONTENT_TYPES.COURSE_TRAILER

  if (content.sample?.mediaUuid) return TRACK_CONTENT_TYPES.COURSE_SAMPLE

  // this shouldn't ever happen, but return COURSE_TRAILER as fallback.
  return TRACK_CONTENT_TYPES.COURSE_TRAILER
}

const getTrailerFromSession = (session: SingleSessionResult) => {
  const mediaId = session.trailer?.mediaUuid
  const instructorName =
    session.instructors?.map((i) => i.name).join(', ') || ''

  return {
    id: session.id,
    uuid: session.uuid,
    instructorAvatarUrl: session.primary1x1,
    instructorName,
    skill: session.title,
    headline: session.title,
    slug: session.slug,
    shortDescription: session.fullDescription || '',
    title: instructorName,
    contentType: getContentType(session),
    mediaId,
    courseUrl: paths.courseTypePage('camp', session.slug),
    contentId: `${session.id}_${mediaId}`,
    projectCount: session.totalProjects || 0,
    lessonCount: undefined,
    formattedDuration: undefined,
    primary16x9: session.primary16x9 || '',
  }
}

const getTrailerFromCourse = (course: Course | CourseDeep) => {
  const mediaId = course.mediaUuid || course.sample?.mediaUuid

  return {
    id: course.id,
    uuid: course.uuid,
    instructorAvatarUrl: course.primary1x1,
    instructorName: course.instructorName,
    skill: course.skill,
    headline:
      formatSubtitle(
        course.title,
        course.instructorName,
        course.isMultiInstructor,
        course.multiInstructorName,
      ) || '',
    slug: course.slug,
    shortDescription: course.marketingCmColumn || '',
    title:
      formatTitle(
        course.isMultiInstructor,
        course.skill,
        course.instructorName,
      ) || '',
    contentType: getContentType(course),
    mediaId,
    courseUrl: paths.courseTypePage(course.type, course.slug),
    contentId: `${course.id}_${mediaId}`,
    lessonCount: course.numChapters || 0,
    projectCount: undefined,
    formattedDuration: formatCourseDuration(course.totalSeconds || 0),
    primary16x9: course.primary16x9 || '',
  }
}

export const getTrailerFromContent = (content: AnyCourse) => {
  if (isSession(content)) {
    return getTrailerFromSession(content)
  }

  return getTrailerFromCourse(content)
}
